/* eslint-disable no-case-declarations */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FATCA_LOADING_KEYS, FATCA_PAGES_KEYS } from '../constants';
import { getUserFatcaState } from '../actions/getUserFatcaState';
import { FATCATaxationOption, FATCAUserDataResponse } from '../types';
import { FATCATranslations } from './getFATCATranslations';
import { sendUserAddressAndGetFATCATaxationOptions } from '../actions/sendUserAddressAndGetFATCATaxationOptions';
import { sendAndValidateTaxationEntries } from '../actions/sendAndValidateTaxationEntries';
import { useRouter } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { FATCAAgreementAction } from '../actions/fatcaAgreementAction';
import { useFATCATaxResidencies } from './useFATCATaxResidencies';
import { sendAndValidateOtherTaxationResidencies } from '../actions/sendAndValidateOtherTaxationResidencies';
import { useHandleGqlErrorsWithSnackbar } from '../../common';
import { getTINReasonOptionsFromResidencyOptions } from '../actions/getTINReasonOptionsFromResidencyOptions';
import { validateFATCATaxResidenciesReasons } from '../actions/validateFATCATaxResidenciesReasons';
import { validateFATCATaxResidenciesTextReasons } from '../actions/validateFATCATaxResidenciesTextReasons';

export const useFATCAFormState = (props: {
  setCurrentPage: Dispatch<SetStateAction<FATCA_PAGES_KEYS>>;
  currentPage: FATCA_PAGES_KEYS;
  translations: FATCATranslations;
  onFATCAModalClose?: () => void;
}) => {
  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const fatcaTaxResidencies = useFATCATaxResidencies();

  const { push } = useRouter();

  const { currentPage, setCurrentPage, translations, onFATCAModalClose } =
    props;

  const [loadingKeys, setLoadingKeys] = useState<FATCA_LOADING_KEYS[]>([
    FATCA_LOADING_KEYS.FATCA_USER_DATA,
  ]);

  const [currentTaxationOptions, setCurrentTaxationOptions] = useState<
    Array<string>
  >([]);

  const [fatcaUserData, setFatcaUserData] =
    useState<FATCAUserDataResponse | null>(null);

  const [taxationOptions, setTaxationOptions] = useState<
    FATCATaxationOption[] | null
  >(null);

  useEffect(() => {
    getUserFatcaState().then((response) => {
      setFatcaUserData(response);

      setLoadingKeys((prev) =>
        prev.filter((key) => key !== FATCA_LOADING_KEYS.FATCA_USER_DATA),
      );
    });
  }, []);

  const onNextStep = useCallback(async () => {
    switch (true) {
      case currentPage === FATCA_PAGES_KEYS.FATCA_USER_DATA && !!fatcaUserData:
        setLoadingKeys((prev) => [
          ...prev,
          FATCA_LOADING_KEYS.FATCA_TAXATION_OPTIONS,
        ]);

        if (!fatcaUserData.address) {
          setCurrentPage(FATCA_PAGES_KEYS.FATCA_USER_DATA);

          break;
        }

        const taxationOptionsResponse =
          await sendUserAddressAndGetFATCATaxationOptions({
            address: fatcaUserData.address,
          });

        setTaxationOptions(taxationOptionsResponse);

        setCurrentPage(FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS);

        setLoadingKeys((prev) =>
          prev.filter(
            (key) => key !== FATCA_LOADING_KEYS.FATCA_TAXATION_OPTIONS,
          ),
        );

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS:
        setLoadingKeys((prev) => [
          ...prev,
          FATCA_LOADING_KEYS.FATCA_LOADING_ACCESS_TO_REAL_STOCKS_STATUS,
        ]);

        const taxationEntriesResponse = await sendAndValidateTaxationEntries({
          taxationEntries: currentTaxationOptions,
        });

        const { realStocksAccessStatus } = taxationEntriesResponse;

        if (realStocksAccessStatus === 'ask-for-other') {
          setCurrentPage(FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB);
        } else if (realStocksAccessStatus === 'no-access-to-real-stocks') {
          setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED);
        } else if (realStocksAccessStatus === 'has-access-to-real-stocks') {
          setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED);
        } else {
          setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED);
        }

        setLoadingKeys((prev) =>
          prev.filter(
            (key) =>
              key !==
              FATCA_LOADING_KEYS.FATCA_LOADING_ACCESS_TO_REAL_STOCKS_STATUS,
          ),
        );

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED:
        onFATCAModalClose?.();

        push(NEXT_ROUTES.PORTFOLIO);

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED:
        const _agreeResponse = await FATCAAgreementAction();

        onFATCAModalClose?.();

        push(NEXT_ROUTES.DISCOVER);

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB:
        setLoadingKeys((prev) => [
          ...prev,
          FATCA_LOADING_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB,
        ]);

        try {
          const residenciesResponse =
            await sendAndValidateOtherTaxationResidencies(
              fatcaTaxResidencies.fatcaTaxResidencies,
            );

          if (residenciesResponse.status === 'some_are_missing_tin_number') {
            // BLOCK FOR GETTING THE REASON OPTIONS AND CHANGING THE PAGE
            const newTaxResidencyOptionsWithReasons =
              await getTINReasonOptionsFromResidencyOptions(
                fatcaTaxResidencies.fatcaTaxResidencies,
              );

            fatcaTaxResidencies.setAllFATCATaxResidencies(
              newTaxResidencyOptionsWithReasons,
            );

            setCurrentPage(FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE);
          } else {
            setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED);
          }
        } catch (error) {
          processErrors((error as any).errors as string[]);
        } finally {
          setLoadingKeys((prev) =>
            prev.filter(
              (key) =>
                key !== FATCA_LOADING_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB,
            ),
          );
        }

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE:
        setLoadingKeys((prev) => [
          ...prev,
          FATCA_LOADING_KEYS.FATCA_MISSING_TIN_REASON_PAGE_VALIDATE_REASONS,
        ]);

        try {
          const validateReasonsResponse =
            await validateFATCATaxResidenciesReasons(
              fatcaTaxResidencies.fatcaTaxResidencies,
            );

          if (validateReasonsResponse.status === 'success') {
            setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED);
          } else {
            fatcaTaxResidencies.setAllFATCATaxResidencies(
              validateReasonsResponse.entries,
            );

            setCurrentPage(FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE);
          }
        } catch (error) {
          processErrors((error as any).errors as string[]);
        } finally {
          setLoadingKeys((prev) =>
            prev.filter(
              (key) =>
                key !==
                FATCA_LOADING_KEYS.FATCA_MISSING_TIN_REASON_PAGE_VALIDATE_REASONS,
            ),
          );
        }

        break;

      case currentPage === FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE:
        setLoadingKeys((prev) => [
          ...prev,
          FATCA_LOADING_KEYS.FATCA_MISSING_TIN_REASON_VALIDATE_TEXT_REASONS,
        ]);

        try {
          const validateTextReasonsResponse =
            await validateFATCATaxResidenciesTextReasons(
              fatcaTaxResidencies.fatcaTaxResidencies,
            );

          if (validateTextReasonsResponse.status === 'success') {
            setCurrentPage(FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED);
          } else {
            throw new Error('Some text reasons are missing');
          }
        } catch (error) {
          processErrors((error as any).errors as string[]);
        } finally {
          setLoadingKeys((prev) =>
            prev.filter(
              (key) =>
                key !==
                FATCA_LOADING_KEYS.FATCA_MISSING_TIN_REASON_VALIDATE_TEXT_REASONS,
            ),
          );
        }

        break;

      default:
        setCurrentPage(FATCA_PAGES_KEYS.FATCA_USER_DATA);

        break;
    }

    return;
  }, [
    currentPage,
    fatcaUserData,
    setCurrentPage,
    setLoadingKeys,
    currentTaxationOptions,
    setCurrentTaxationOptions,
    setFatcaUserData,
    setTaxationOptions,
    translations,
    fatcaTaxResidencies,
  ]);

  const sectionTitleAndDescription:
    | undefined
    | null
    | {
        title: string | null;
        subtitle: string | null;
        description?: string | null;
      } = useMemo(() => {
    switch (currentPage) {
      case FATCA_PAGES_KEYS.FATCA_USER_DATA:
        return {
          title: translations.title_confirmDetails,
          subtitle: translations.subtitle_confirmDetails,
        };

      case FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS:
        return {
          title: translations.title_subjectToTaxation,
          subtitle: translations.subtitle_subjectToTaxation,
        };

      case FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED:
        return {
          title: translations.title_self_ceritification,
          subtitle: translations.content_self_ceritification,
        };

      case FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB:
        return {
          title: translations.title_tax_residency_hub,
          subtitle: translations.subtitle_tax_residency_hub,
        };

      case FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED:
        return {
          title: translations.title_no_access_to_real_stocks,
          subtitle: translations.content_no_access_to_real_stocks,
        };

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE:
        return undefined;

      default:
        return {
          title: '',
          subtitle: '',
          description: null,
        };
    }
  }, [currentPage]);

  const stepsInformation = useMemo(() => {
    let totalSteps = 0;

    let currentStep = 0;

    switch (currentPage) {
      case FATCA_PAGES_KEYS.FATCA_USER_DATA:
        totalSteps = 2;

        currentStep = 0;

        break;

      case FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS:
        totalSteps = 2;

        currentStep = 2;

        break;

      case FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED:
        totalSteps = 3;

        currentStep = 3;

        break;

      case FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED:
        totalSteps = 3;

        currentStep = 3;

        break;

      case FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB:
        totalSteps = 4;

        currentStep = 2;

        break;

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE:
        totalSteps = 4;

        currentStep = 3;

        break;

      default:
        totalSteps = 0;

        currentStep = 0;

        break;
    }

    return {
      totalSteps,
      currentStep,
    };
  }, [currentPage]);

  const loading = loadingKeys.length > 0;

  return {
    stepsInformation,
    loading,
    fatcaUserData,
    onNextStep,
    taxationOptions,
    sectionTitleAndDescription,
    currentTaxationOptions,
    setCurrentTaxationOptions,
    fatcaTaxResidencies,
  };
};
